(() => {
  'use strict';
  if (window.MaxCardVideo) return;
  const configElement = document.getElementById('mx-card-video-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const cache = new Map();
  const pending = new Set();
  const lifetime = 60000;
  let scheduled = false;
  let dialog;
  let opener;
  let scrollPosition;

  function safeVideo(value) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return '';
      if (!['youtube.com', 'www.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(url.hostname)) return '';
      const match = /^\/embed\/([A-Za-z0-9_-]{11})$/.exec(url.pathname);
      return match ? 'https://www.youtube-nocookie.com/embed/' + match[1] : '';
    } catch { return ''; }
  }

  function chip(url, title, productUrl) {
    const link = document.createElement('a');
    link.className = 'mx-card-video';
    link.href = productUrl;
    link.dataset.mxCardVideo = url;
    link.dataset.mxVideoTitle = title;
    link.setAttribute('aria-label', config.watch.replace('__TITLE__', title));
    link.setAttribute('aria-haspopup', 'dialog');
    link.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.2a.7.7 0 0 1 1.05-.61l7.4 4.3a1.3 1.3 0 0 1 0 2.22l-7.4 4.3A.7.7 0 0 1 5 12.8Z" fill="currentColor"/></svg><span></span>';
    link.querySelector('span').textContent = config.label;
    return link;
  }

  function paint() {
    document.querySelectorAll('max-api-collection[data-active="true"] [data-max-card-handle]').forEach(item => {
      const record = cache.get(item.dataset.maxCardHandle);
      if (!record || Date.now() - record.at > lifetime) return;
      const footer = item.querySelector('.card-footer');
      if (!footer) return;
      const existing = footer.querySelector('.mx-card-video');
      if (!record.url) { existing?.remove(); return; }
      if (existing?.dataset.mxCardVideo === record.url) return;
      existing?.remove();
      const title = item.querySelector('.card-heading')?.textContent.trim() || '';
      const href = item.querySelector('a.js-product-link')?.getAttribute('href') || '#';
      footer.prepend(chip(record.url, title, href));
    });
  }

  async function lookup(handles) {
    handles.forEach(handle => pending.add(handle));
    try {
      const url = new URL(config.searchUrl, location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('view', 'max-card-videos');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', 'mxv1~' + handles.join(','));
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(6000) });
      if (!response.ok) throw new Error('Video lookup failed');
      const data = await response.json();
      if (data.version !== 1 || !data.videos || typeof data.videos !== 'object') throw new Error('Invalid video response');
      handles.forEach(handle => cache.set(handle, { at: Date.now(), url: safeVideo(data.videos[handle]) }));
      if (cache.size > 400) for (const key of [...cache.keys()].slice(0, cache.size - 400)) cache.delete(key);
      paint();
    } catch {
      handles.forEach(handle => cache.set(handle, { at: Date.now(), url: '' }));
    } finally { handles.forEach(handle => pending.delete(handle)); }
  }

  function scan() {
    scheduled = false;
    const handles = new Set();
    document.querySelectorAll('max-api-collection[data-active="true"] [data-max-card-handle]').forEach(item => {
      const handle = item.dataset.maxCardHandle;
      if (!handle || handle.length > 255 || !/^[a-z0-9-]+$/.test(handle) || !item.querySelector('.card-footer')) return;
      const record = cache.get(handle);
      if ((!record || Date.now() - record.at > lifetime) && !pending.has(handle)) handles.add(handle);
    });
    paint();
    const batch = [...handles];
    for (let i = 0; i < batch.length; i += 20) lookup(batch.slice(i, i + 20));
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  }

  function open(link) {
    const url = safeVideo(link.dataset.mxCardVideo);
    if (!url || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return false;
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'mx-video-dialog';
      dialog.setAttribute('aria-labelledby', 'mx-video-dialog-title');
      dialog.innerHTML = '<div class="mx-video-dialog__header"><h2 id="mx-video-dialog-title" class="mx-video-dialog__title"></h2><button type="button" class="mx-video-dialog__close" autofocus>×</button></div><div data-mx-video-player></div><a class="mx-video-dialog__fallback" target="_blank" rel="noopener noreferrer"></a>';
      dialog.querySelector('button').setAttribute('aria-label', config.close);
      dialog.querySelector('button').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialog.querySelector('[data-mx-video-player]').replaceChildren();
        document.documentElement.classList.remove('mx-video-open');
        if (opener?.isConnected) opener.focus({ preventScroll: true });
        if (scrollPosition) requestAnimationFrame(() => window.scrollTo({ ...scrollPosition, behavior: 'instant' }));
      });
      document.body.append(dialog);
    }
    if (dialog.open) return true;
    opener = link;
    scrollPosition = { left: window.scrollX, top: window.scrollY };
    const title = link.dataset.mxVideoTitle || config.label;
    dialog.querySelector('h2').textContent = title;
    const iframe = document.createElement('iframe');
    iframe.className = 'mx-video-dialog__player';
    iframe.title = config.watch.replace('__TITLE__', title);
    iframe.src = url + '?autoplay=1&playsinline=1&rel=0';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    dialog.querySelector('[data-mx-video-player]').replaceChildren(iframe);
    const fallback = dialog.querySelector('.mx-video-dialog__fallback');
    fallback.textContent = config.fallback;
    fallback.href = 'https://www.youtube.com/watch?v=' + url.split('/').pop();
    document.documentElement.classList.add('mx-video-open');
    dialog.showModal();
    return true;
  }

  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-mx-card-video]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (open(link)) { event.preventDefault(); event.stopPropagation(); }
  }, true);
  const root = document.getElementById('MainContent');
  if (root) new MutationObserver(records => {
    if (records.some(record => record.type === 'attributes' || [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-max-card-handle], max-api-collection') || node.querySelector('[data-max-card-handle]'))))) schedule();
  }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-active'] });
  window.MaxCardVideo = Object.freeze({ refresh: schedule });
  window.addEventListener('pageshow', schedule);
  schedule();
})();
